import React from 'react';
import {
	addProject,
	getCarType,
	getFilter,
	setFilterResultsNumber,
	getProjectsToProcess,
	csvTypeConverter,
	getSearchFieldName
} from './geocmsUtil';
import { useNormalResult } from './normalizeResults';
import {
	AGENCY_PROJECT,
	CAR_TYPES,
	PROJECT_CAR,
	PROJECT_CRI,
	SEARCH_RADIUS,
	SUB_AGENCY_PROJECT,
	CURRENT_CRI_LIST,
	PROJECT_CCG,
	PROJECT_CGL,
	PROJECT_CSV,
	CSV_TYPES,
	WEB_FORM_EVENT_NAME,
	PROJECT_CVC,
	PROJECT_CCC,
	PROJECT_CGR,
	PROJECT_SOS,
	PROJECT_DOG,
	DATA_SRC_TYPES,
	FILIALI_PROJECT,
	PROJECT_GRA,
	CURRENT_GRA_LIST,
	GEO_CMS_API_BASE_URL,
	PROJECT_BSG,
	PROJECT_EUG,
	PROJECT_HGG,
	PROJECT_UGG,
	PROJECT_ASG,
	PROJECT_ING,
	PROJECT_NSG,
	PROJECT_STS,
	CAR_CERTIFICATIONS
} from '../utils/Constants';
import { calcDistanceMatrix, useGoogleMapsApi } from './gis';
import useFilters from '../useFilters';
import { useInputParams } from '../utils/InputParamsProvider';
import { useDataSources } from './dataSources';
import { SHOW_ALL, TABS_TO_DISPLAY } from './portletConfiguration';

const GeoCMSApiKeyContext = React.createContext();

export function GeoCMSApiKeyProvider({
	license,
	children,
	apiVersion,
	project,
	modules
}) {
	const [api, setApi] = React.useState(null);
	const licenseRef = React.useRef(license);

	const google = useGoogleMapsApi();

	if (process.env.NODE_ENV !== 'production') {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		React.useEffect(() => {
			if (license !== licenseRef.current) {
				console.error('GeoCMS license cannot change at run-time!');
			}
		}, [license]);
	}

	React.useEffect(() => {
		if (!google) return;

		const uuid = generateCallbackName();

		window[uuid] = () => {
			setApi(window.scube);
			delete window[uuid];
		};

		const url = new URL('js', GEO_CMS_API_BASE_URL);

		url.searchParams.append('v', String(apiVersion));
		url.searchParams.append('project', String(project));
		url.searchParams.append('modules', String(modules));
		url.searchParams.append('callback', uuid);

		if (licenseRef.current) {
			url.searchParams.append('license', String(license));
		}

		const script = document.createElement('script');

		script.src = String(url);

		document.head.appendChild(script);

		return () => {
			delete window[uuid];
		};
	}, [google]);

	return <GeoCMSApiKeyContext.Provider children={children} value={api} />;
}

export function useGeoCMSApi() {
	return React.useContext(GeoCMSApiKeyContext);
}

export function useGeoCMSMap(googleMap) {
	const google = useGoogleMapsApi();
	const geoCMS = useGeoCMSApi();

	const [geoCMSMap, setGeoCMSMap] = React.useState();

	React.useEffect(() => {
		if (geoCMS != null && googleMap != null) {
			setGeoCMSMap(
				new geoCMS.geocms.GeoMap(googleMap, {
					overlayNode: document.createElement('div'),
					encoding: 'UTF-8'
				})
			);
		}
	}, [googleMap, geoCMS]);

	// Adding projects to geoCMSMap...
	React.useEffect(() => {
		if (!geoCMSMap || !google) return;

		if (TABS_TO_DISPLAY.includes(AGENCY_PROJECT)) {
			// AGENZIE
			addProject(geoCMSMap, AGENCY_PROJECT);
			addProject(geoCMSMap, SUB_AGENCY_PROJECT);
		} else if (TABS_TO_DISPLAY.includes(FILIALI_PROJECT)) {
			// FILIALI MPS
			addProject(geoCMSMap, FILIALI_PROJECT);
		}
		if (TABS_TO_DISPLAY.includes(PROJECT_CAR)) {
			// CARROZZERIE
			addProject(geoCMSMap, PROJECT_CAR);
		}
		if (TABS_TO_DISPLAY.includes(PROJECT_CRI)) {
			// CRISTALLI
			// CarGlass
			addProject(geoCMSMap, PROJECT_CCG);
			// GlassDrive
			addProject(geoCMSMap, PROJECT_CGL);
			//Vetrocar
			addProject(geoCMSMap, PROJECT_CVC);
		}
		if (TABS_TO_DISPLAY.includes(PROJECT_GRA)) {
			// GRANDINE
			// Carclinic
			addProject(geoCMSMap, PROJECT_CCC);
			// Maestri della grandine
			addProject(geoCMSMap, PROJECT_CGR);
			// Dottor Grandine
			addProject(geoCMSMap, PROJECT_DOG);
			// Ballsystem
			addProject(geoCMSMap, PROJECT_BSG);
			// Eurograndine
			addProject(geoCMSMap, PROJECT_EUG);
			// HailGuard
			addProject(geoCMSMap, PROJECT_HGG);
			// UpGrandine
			addProject(geoCMSMap, PROJECT_UGG);
			// Autosicura
			addProject(geoCMSMap, PROJECT_ASG);
			// Inova
			addProject(geoCMSMap, PROJECT_ING);
			// Non Solo Grandine
			addProject(geoCMSMap, PROJECT_NSG); // TODO: attivare
			// Storm Solution
			addProject(geoCMSMap, PROJECT_STS); // TODO: attivare
		}
		if (TABS_TO_DISPLAY.includes(PROJECT_CSV)) {
			// CENTRI SALUTE
			addProject(geoCMSMap, PROJECT_CSV);
		}
	}, [geoCMSMap, google]);

	return [geoCMSMap];
}

export function useGeoCmsSearch(
	geoCMSMap,
	searchFor,
	searchParameters,
	setLoading
) {
	const google = useGoogleMapsApi();

	const [, getDataSource] = useDataSources();

	const [, normalizeResults] = useNormalResult();

	const [searchResults, setSearchResults] = React.useState(null);

	const [fakeParam, setFakeParam] = React.useState(0);

	const refreshSearch = React.useCallback(() => {
		setFakeParam((prev) => prev + 1);
	}, [setFakeParam]);

	const [filters, setFilters] = useFilters(refreshSearch);
	const [atLeastOne, setAtLeastOne] = React.useState(false);

	const dataSrc = getDataSource(searchFor);

	React.useEffect(() => {
		if (
			!(
				geoCMSMap &&
				searchParameters &&
				searchFor &&
				dataSrc.type === DATA_SRC_TYPES.GeoCMS
			)
		) {
			return;
		}

		const lat = searchParameters?.lat;
		const lng = searchParameters?.lng;

		const searchParams = {
			maxZoomLevel: 17,
			point: {
				lat: lat,
				lng: lng
			},
			clear: true,
			frontend: 'quick',
			maxResult: atLeastOne ? 1 : 30,
			distanza: atLeastOne ? null : SEARCH_RADIUS,
			focus: atLeastOne ? true : false
		};

		const totalResults = [];

		const projectsToProcess = getProjectsToProcess(searchFor, atLeastOne);

		let processedProjects = 0;

		const agencyNameIdMap = [];

		function setSearchResultProcess(results) {
			for (let i = 0; i < results.length; i++) {
				const result = results[i];

				if (result.subAgencyId) {
					const agencyNameId = agencyNameIdMap.filter((nameId) => {
						return nameId.id === result.agencyId;
					});
					if (agencyNameId.length > 0)
						result.name =
							'Subagenzia AXA - ' + agencyNameId[0].name;
				}
			}
			setSearchResults(results);
		}

		if (
			searchFor === AGENCY_PROJECT ||
			(SHOW_ALL && TABS_TO_DISPLAY.includes(AGENCY_PROJECT))
		) {
			setLoading(true);
			const projects = atLeastOne
				? [AGENCY_PROJECT]
				: [AGENCY_PROJECT, SUB_AGENCY_PROJECT];

			for (let i = 0; i < projects.length; i++) {
				const project = projects[i];

				const agencySearchParams = { ...searchParams };

				if (project === SUB_AGENCY_PROJECT) {
					agencySearchParams.query = '[siti_axa]=[S]';
				}

				try {
					geoCMSMap
						.getProject(project)
						.searchData(agencySearchParams, function (response) {
							const results = response.results;
							const size = results.size();

							const filter = getFilter(
								filters[AGENCY_PROJECT],
								project
							);

							for (let i = 0; i < size; i++) {
								const result = results.getAt(i);
								const data = result.getUserdata();

								// DEBUG: Log del DTO grezzo di GeoCMS (pre-mapping) - AGENCY
								if (i === 0) {
									console.debug('=== GeoCMS RAW DTO AGENCY (first result) ===');
									console.debug('result object:', result);
									console.debug('getUserdata():', data);
									console.debug('getUserdataGroups():', result.getUserdataGroups?.() || 'N/A');
									console.debug('=== END RAW DTO ===');
								}

								if (filter.value) {
									totalResults.push(result);
									if (project === AGENCY_PROJECT) {
										agencyNameIdMap.push({
											id: data['IDAgenzia'],
											name: data['Intestatario mandato'],
											IDSede: data['IDSede']
										});
									}
								}

								result.hide();
							}

							setFilterResultsNumber(filter, size);
							setFilters(filters, false);

							if (processedProjects === projectsToProcess - 1) {
								try {
									if (totalResults.length > 0) {
										calcDistanceMatrix(
											google,
											totalResults,
											0,
											lat,
											lng,
											setSearchResultProcess,
											setLoading,
											normalizeResults
										);
									} else if (!atLeastOne) {
										setSearchResultProcess([]);
										setLoading(false);
									}
								} catch (e) {
									console.error(
										'Error in calcDistanceMatrix',
										e
									);
									setSearchResultProcess(
										normalizeResults(totalResults)
									);
									setLoading(false);
								}
							} else {
								processedProjects++;
							}
						});
				} catch (e) {
					console.error(
						'Error in geoCMS Search for project',
						project
					);
					if (processedProjects === projectsToProcess - 1) {
						setSearchResultProcess(normalizeResults(totalResults));
						setLoading(false);
					} else {
						processedProjects++;
					}
				}
			}
		}
		if (
			searchFor === FILIALI_PROJECT ||
			(SHOW_ALL && TABS_TO_DISPLAY.includes(FILIALI_PROJECT))
		) {
			setLoading(true);

			searchParams['ckB'] = false;

			try {
				geoCMSMap
					.getProject(FILIALI_PROJECT)
					.searchData(searchParams, function (response) {
						const results = response.results;

						for (let i = 0; i < results.size(); i++) {
							const result = results.getAt(i);

							totalResults.push(result);

							result.hide();
						}

						if (processedProjects === projectsToProcess - 1) {
							try {
								if (totalResults.length > 0) {
									calcDistanceMatrix(
										google,
										totalResults,
										0,
										lat,
										lng,
										setSearchResultProcess,
										setLoading,
										normalizeResults
									);
								} else {
									setSearchResultProcess([]);
									setLoading(false);
								}
							} catch (e) {
								console.error('Error in calcDistanceMatrix', e);
								setSearchResultProcess(
									normalizeResults(totalResults)
								);
								setLoading(false);
							}
						} else {
							processedProjects++;
						}
					});
			} catch (e) {
				console.error(
					'Error in geoCMS Search for project',
					PROJECT_CAR
				);
				if (processedProjects === projectsToProcess - 1) {
					setSearchResultProcess(normalizeResults(totalResults));
					setLoading(false);
				} else {
					processedProjects++;
				}
			}
		}
		if (
            searchFor === PROJECT_CAR ||
            (SHOW_ALL && TABS_TO_DISPLAY.includes(PROJECT_CAR))
        ) {
            setLoading(true);
            try {
                geoCMSMap
                    .getProject(PROJECT_CAR)
                    .searchData(searchParams, function (response) {
                        const results = response.results;

                        // evolutiva BMW
                        CAR_CERTIFICATIONS.forEach((cert) => {
                            if (!filters[PROJECT_CAR].find((f) => f.key === cert.name)) {
                                filters[PROJECT_CAR].push({
                                    key: cert.name,
                                    label: cert.name,
                                    value: false,
                                    resultsNumber: 0,
                                    icon: cert.icon
                                });
                            }
                        });

                        const carSearchInfo = {
                            [CAR_TYPES.NETWORK_AXA]: {
                                filter: getFilter(
                                    filters[PROJECT_CAR],
                                    CAR_TYPES.NETWORK_AXA
                                ),
                                number: 0
                            },
                            [CAR_TYPES.CARCLINIC]: {
                                filter: getFilter(
                                    filters[PROJECT_CAR],
                                    CAR_TYPES.CARCLINIC
                                ),
                                number: 0
                            },
                            [CAR_TYPES.SOLUZIONE_CARROZZERIE]: {
                                filter: getFilter(
                                    filters[PROJECT_CAR],
                                    CAR_TYPES.SOLUZIONE_CARROZZERIE
                                ),
                                number: 0
                            },
                            [CAR_TYPES.METAFORA]: {
                                filter: getFilter(
                                    filters[PROJECT_CAR],
                                    CAR_TYPES.METAFORA
                                ),
                                number: 0
                            },
                            [CAR_TYPES.AUTOSICURA]: {
                                filter: getFilter(
                                    filters[PROJECT_CAR],
                                    CAR_TYPES.AUTOSICURA
                                ),
                                number: 0
                            },
                            [CAR_TYPES.RIPARTE]: {
                                filter: getFilter(
                                    filters[PROJECT_CAR],
                                    CAR_TYPES.RIPARTE
                                ),
                                number: 0
                            }
                        };

                        // evolutiva BMW: contatori e gli stati per le certificazioni 
                        const certSearchInfo = CAR_CERTIFICATIONS.map(cert => ({
                            name: cert.name,
                            filter: getFilter(filters[PROJECT_CAR], cert.name),
                            number: 0
                        }));

                        // evolutiva BMW
                        const activeCerts = certSearchInfo.filter(c => c.filter && c.filter.value);

                        for (let i = 0; i < results.size(); i++) {
                            const result = results.getAt(i);
                            const data = result.getUserdata();
                            const type = getCarType(data);

                            if (carSearchInfo[type]) {
                                carSearchInfo[type].number += 1;
                            }

                            // evolutiva BMW
                            certSearchInfo.forEach(c => {
                                if (data[c.name] === 'SI') {
                                    c.number += 1;
                                }
                            });

                            // evolutiva BMW
                            const passesNetworkFilter = carSearchInfo[type]?.filter?.value;
                            
                            let passesCertFilter = true;
                            if (activeCerts.length > 0) {
                                passesCertFilter = activeCerts.some(c => data[c.name] === 'SI');
                            }

                            if (passesNetworkFilter && passesCertFilter) {
                                totalResults.push(result);
                            }

                            result.hide();
                        }

                        setFilterResultsNumber(
                            carSearchInfo[CAR_TYPES.NETWORK_AXA]?.filter,
                            carSearchInfo[CAR_TYPES.NETWORK_AXA]?.number
                        );
                        setFilterResultsNumber(
                            carSearchInfo[CAR_TYPES.CARCLINIC]?.filter,
                            carSearchInfo[CAR_TYPES.CARCLINIC]?.number
                        );
                        setFilterResultsNumber(
                            carSearchInfo[CAR_TYPES.SOLUZIONE_CARROZZERIE]
                                ?.filter,
                            carSearchInfo[CAR_TYPES.SOLUZIONE_CARROZZERIE]
                                ?.number
                        );
                        setFilterResultsNumber(
                            carSearchInfo[CAR_TYPES.METAFORA]?.filter,
                            carSearchInfo[CAR_TYPES.METAFORA]?.number
                        );
                        setFilterResultsNumber(
                            carSearchInfo[CAR_TYPES.AUTOSICURA]?.filter,
                            carSearchInfo[CAR_TYPES.AUTOSICURA]?.number
                        );
                        setFilterResultsNumber(
                            carSearchInfo[CAR_TYPES.RIPARTE]?.filter,
                            carSearchInfo[CAR_TYPES.RIPARTE]?.number
                        );

                        // evolutiva BMW
                        certSearchInfo.forEach(c => {
                            setFilterResultsNumber(c.filter, c.number);
                        });

                        setFilters(filters, false);

                        if (processedProjects === projectsToProcess - 1) {
                            try {
                                if (totalResults.length > 0) {
                                    calcDistanceMatrix(
                                        google,
                                        totalResults,
                                        0,
                                        lat,
                                        lng,
                                        setSearchResultProcess,
                                        setLoading,
                                        normalizeResults
                                    );
                                } else {
                                    setSearchResultProcess([]);
                                    setLoading(false);
                                }
                            } catch (e) {
                                console.error('Error in calcDistanceMatrix', e);
                                setSearchResultProcess(
                                    normalizeResults(totalResults)
                                );
                                setLoading(false);
                            }
                        } else {
                            processedProjects++;
                        }
                    });
            } catch (e) {
                console.error(
                    'Error in geoCMS Search for project',
                    PROJECT_CAR
                );
                if (processedProjects === projectsToProcess - 1) {
                    setSearchResultProcess(normalizeResults(totalResults));
                    setLoading(false);
                } else {
                    processedProjects++;
                }
            }
        }
		// Blocco di ricerca -> CRISTALLI
		if (
			searchFor === PROJECT_CRI ||
			(SHOW_ALL && TABS_TO_DISPLAY.includes(PROJECT_CRI))
		) {
			setLoading(true);
			// Recupero la lista di progetti su cui cercare
			const projects = CURRENT_CRI_LIST;

			// Eredito ed estendo i parametri di ricerca
			const criSearchParams = { ...searchParams };

			criSearchParams.maxResult = 10;

			// Per ogni project...
			for (let i = 0; i < projects.length; i++) {
				const project = projects[i];

				try {
					// ... faccio una ricerca
					geoCMSMap
						.getProject(project)
						.searchData(criSearchParams, function (response) {
							// Recupero la lista di risultati dalla response
							const results = response.results;
							// Recupero il numero di risultati
							const size = results.size();
							let _size = size;

							// Recupero il filtro per il project corrente
							const filter = getFilter(
								filters[PROJECT_CRI],
								project
							);

							// Per ogni risultato...
							for (let i = 0; i < size; i++) {
								const result = results.getAt(i);
								// Recupero l'oggetto che contiene le informazioni del singolo result
								const data = result.getUserdata();

								let exclude = false;

								// I centri carGlass provengono da servizi esterni e il parametro di
								// distanza viene ignorato.
								// Lo snippet di seguito esclude i centri carGlass piu lontani di 50 Km
								if (project === PROJECT_CCG) {
									// Recupero la distanza
									var dist = data.distance;
									dist = parseFloat(dist);

									if (dist > 50 || dist === 0) {
										exclude = true;
										_size--;
									}
								}

								if (filter.value && !exclude) {
									// totalResults conterrà tutti i risultati che devono essere mostrati
									totalResults.push(result);
								}

								// Impedisce a geoCMS di mettere i risultati in mappa
								result.hide();
							}

							// Numero di risultati per quel project nella modalina dei filtri
							setFilterResultsNumber(filter, _size);
							// Aggiorna lo stato dei filtri per modificare il resultsNumber. SENZA refresh della ricerca
							setFilters(filters, false);

							// Controllo se ho processato tutti i project...
							if (processedProjects === projectsToProcess - 1) {
								try {
									// ... e se c'è almeno un risultato
									if (totalResults.length > 0) {
										// Lancio la calcDistanceMatrix
										// Per ogni oggetto in totalResults calcola la distanza dal
										// punto di ricerca, e poi NORMALIZZA tutti i risultati
										// tramite la funzione passata come ultimo parametro
										calcDistanceMatrix(
											google,
											totalResults,
											0,
											lat,
											lng,
											setSearchResultProcess,
											setLoading,
											normalizeResults
										);
									} else {
										setSearchResultProcess([]);
										setLoading(false);
									}
								} catch (e) {
									console.error(
										'Error in calcDistanceMatrix',
										e
									);
									setSearchResultProcess(
										normalizeResults(totalResults)
									);
									setLoading(false);
								}
							} else {
								processedProjects++;
							}
						});
				} catch (e) {
					console.error(
						'Error in geoCMS Search for project',
						project
					);
					if (processedProjects === projectsToProcess - 1) {
						setSearchResultProcess(normalizeResults(totalResults));
						setLoading(false);
					} else {
						processedProjects++;
					}
				}
			}
		}
		// Blocco di ricerca -> GRANDINE
		if (
			searchFor === PROJECT_GRA ||
			(SHOW_ALL && TABS_TO_DISPLAY.includes(PROJECT_GRA))
		) {
			setLoading(true);
			const projects = CURRENT_GRA_LIST;

			const graSearchParams = { ...searchParams };

			graSearchParams.maxResult = 10;

			for (let i = 0; i < projects.length; i++) {
				const project = projects[i];

				try {
					geoCMSMap
						.getProject(project)
						.searchData(graSearchParams, function (response) {
							const results = response.results;

							const size = results.size();

							const filter = getFilter(
								filters[PROJECT_GRA],
								project
							);

							for (let i = 0; i < size; i++) {
								const result = results.getAt(i);

								if (filter.value) {
									totalResults.push(result);
								}

								result.hide();
							}

							setFilterResultsNumber(filter, size);
							setFilters(filters, false);

							if (processedProjects === projectsToProcess - 1) {
								try {
									if (totalResults.length > 0) {
										calcDistanceMatrix(
											google,
											totalResults,
											0,
											lat,
											lng,
											setSearchResultProcess,
											setLoading,
											normalizeResults
										);
									} else {
										setSearchResultProcess([]);
										setLoading(false);
									}
								} catch (e) {
									console.error(
										'Error in calcDistanceMatrix',
										e
									);
									setSearchResultProcess(
										normalizeResults(totalResults)
									);
									setLoading(false);
								}
							} else {
								processedProjects++;
							}
						});
				} catch (e) {
					console.error(
						'Error in geoCMS Search for project',
						project
					);
					if (processedProjects === projectsToProcess - 1) {
						setSearchResultProcess(normalizeResults(totalResults));
						setLoading(false);
					} else {
						processedProjects++;
					}
				}
			}
		}
		if (
			searchFor === PROJECT_CSV ||
			(SHOW_ALL && TABS_TO_DISPLAY.includes(PROJECT_CSV))
		) {
			setLoading(true);

			try {
				geoCMSMap
					.getProject(PROJECT_CSV)
					.searchData(searchParams, function (response) {
						const results = response.results;

						const csvSearchInfo = {
							[CSV_TYPES.caseDiCura.class]: {
								filter: getFilter(
									filters[PROJECT_CSV],
									CSV_TYPES.caseDiCura.class
								),
								number: 0
							},
							[CSV_TYPES.centriSpecialistici.class]: {
								filter: getFilter(
									filters[PROJECT_CSV],
									CSV_TYPES.centriSpecialistici.class
								),
								number: 0
							},
							[CSV_TYPES.centriOdontoiatrici.class]: {
								filter: getFilter(
									filters[PROJECT_CSV],
									CSV_TYPES.centriOdontoiatrici.class
								),
								number: 0
							},
							[CSV_TYPES.ospedali.class]: {
								filter: getFilter(
									filters[PROJECT_CSV],
									CSV_TYPES.ospedali.class
								),
								number: 0
							}
						};

						for (let i = 0; i < results.size(); i++) {
							const result = results.getAt(i);
							const data = result.getUserdata();

							const type = csvTypeConverter(
								data['TIPO STRUTTURA']
							).class;

							csvSearchInfo[type].number =
								csvSearchInfo[type].number + 1;

							if (csvSearchInfo[type]?.filter?.value) {
								totalResults.push(result);
							}

							result.hide();
						}

						setFilterResultsNumber(
							csvSearchInfo[CSV_TYPES.caseDiCura.class]?.filter,
							csvSearchInfo[CSV_TYPES.caseDiCura.class]?.number
						);
						setFilterResultsNumber(
							csvSearchInfo[CSV_TYPES.centriSpecialistici.class]
								?.filter,
							csvSearchInfo[CSV_TYPES.centriSpecialistici.class]
								?.number
						);
						setFilterResultsNumber(
							csvSearchInfo[CSV_TYPES.centriOdontoiatrici.class]
								?.filter,
							csvSearchInfo[CSV_TYPES.centriOdontoiatrici.class]
								?.number
						);
						setFilterResultsNumber(
							csvSearchInfo[CSV_TYPES.ospedali.class]?.filter,
							csvSearchInfo[CSV_TYPES.ospedali.class]?.number
						);
						setFilters(filters, false);

						if (processedProjects === projectsToProcess - 1) {
							try {
								if (totalResults.length > 0) {
									calcDistanceMatrix(
										google,
										totalResults,
										0,
										lat,
										lng,
										setSearchResultProcess,
										setLoading,
										normalizeResults
									);
								} else {
									setSearchResultProcess([]);
									setLoading(false);
								}
							} catch (e) {
								console.error('Error in calcDistanceMatrix', e);
								setSearchResultProcess(
									normalizeResults(totalResults)
								);
								setLoading(false);
							}
						} else {
							processedProjects++;
						}
					});
			} catch (e) {
				console.error(
					'Error in geoCMS Search for project',
					PROJECT_CSV
				);
				if (processedProjects === projectsToProcess - 1) {
					setSearchResultProcess(normalizeResults(totalResults));
					setLoading(false);
				} else {
					processedProjects++;
				}
			}
		}
	}, [geoCMSMap, searchParameters, searchFor, fakeParam]);

	return [
		searchResults,
		refreshSearch,
		filters,
		setFilters,
		atLeastOne,
		setAtLeastOne
	];
}

export function useOpenWebForm() {
	const [, PRODUCT_NAME] = useInputParams();

	const openWebFormFn = React.useCallback((result) => {
		if (result) {
			if (window.Liferay)
				window.Liferay.fire(WEB_FORM_EVENT_NAME, {
					id_agenzia: '00' + result.agencyId,
					id_sede: result.siteId ? '00' + result.siteId : null,
					email_istituzionale: result.email,
					intestatario_mandato: result.name,
					indirizzo_request: result.address,
					nome_comune_request: result.city,
					cap_request: result.cap,
					provincia_request: result.prov,
					telefono_request: result.phone,
					fax_request: result.fax,
					prodotto: PRODUCT_NAME
				});
			else console.log('Liferay is not defined...');
		}
	}, []);

	return openWebFormFn;
}

export function generateCallbackName() {
	let uuid;

	do {
		uuid = 'CB_' + Math.round(Math.random() * Number.MAX_SAFE_INTEGER);
	} while (uuid in window);

	return uuid;
}

export function useAutocompleteResultSearch(
	geoCMSMap,
	searchFor,
	searchParameters,
	stringToSearch,
	setLoading
) {
	const google = useGoogleMapsApi();

	const [, normalizeResults] = useNormalResult();

	const [searchResults, setSearchResults] = React.useState(null);

	React.useEffect(() => {
		if (
			!(
				geoCMSMap &&
				searchParameters &&
				searchFor &&
				stringToSearch &&
				stringToSearch.length > 2
			) ||
			searchFor !== PROJECT_CSV
		) {
			return;
		}

		const lat = searchParameters?.lat;
		const lng = searchParameters?.lng;

		const geoCMSSearchParams = {
			maxZoomLevel: 17,
			point: {
				lat: lat,
				lng: lng
			},
			clear: false,
			frontend: 'quick',
			maxResult: 5,
			distanza: 1500000,
			focus: false
		};

		const totalResults = [];

		let processedProjects = 0;

		let projectsToProcess =
			searchFor === PROJECT_CRI ? CURRENT_CRI_LIST : [searchFor];

		projectsToProcess = projectsToProcess.filter((project) => {
			return project !== PROJECT_CCG;
		});

		try {
			for (let i = 0; i < projectsToProcess.length; i++) {
				const project = projectsToProcess[i];

				const searchFieldName = getSearchFieldName(project);

				geoCMSSearchParams.query =
					'[' + searchFieldName + '] LIKE [%' + stringToSearch + '%]';

				geoCMSMap
					.getProject(project)
					.searchData(geoCMSSearchParams, function (response) {
						const results = response.results;
						const size = results.size();

						for (let i = 0; i < size; i++) {
							const result = results.getAt(i);

							totalResults.push(result);

							result.hide();
						}

						if (
							processedProjects ===
							projectsToProcess.length - 1
						) {
							try {
								if (totalResults.length > 0) {
									calcDistanceMatrix(
										google,
										totalResults,
										0,
										lat,
										lng,
										setSearchResults,
										setLoading,
										normalizeResults
									);
								} else {
									setSearchResults([]);
									setLoading(false);
								}
							} catch (e) {
								console.error(
									'Error in calcDistanceMatrix',
									e.message
								);
								setSearchResults(
									normalizeResults(totalResults)
								);
							}
						} else {
							processedProjects++;
						}
					});
			}
		} catch (e) {
			console.error('Error in geoCMS Search for project', searchFor);
			processedProjects++;
		}

		let ignore = false;

		return () => {
			ignore = true;
		};
	}, [geoCMSMap, searchParameters, searchFor, stringToSearch]);

	return [searchResults];
}
